import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, mergeProps as _mergeProps, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withKeys as _withKeys, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = ["id"];
const _hoisted_2 = { class: "text-sm text-(--colors-text-body-subtle) mb-4" };
const _hoisted_3 = {
    class: "mt-1 text-sm text-(--colors-text-body-subtle)",
    "data-testid": "collecte-form-create-slug-hint"
};
const _hoisted_4 = {
    class: "mt-1 text-sm text-(--colors-text-body-subtle)",
    "data-testid": "collecte-form-create-campaign-hint"
};
const _hoisted_5 = { class: "block text-sm font-medium text-(--colors-text-black-default) mb-2" };
const _hoisted_6 = { class: "flex flex-wrap gap-4" };
const _hoisted_7 = {
    key: 0,
    role: "alert",
    class: "text-sm text-(--colors-text-error)",
    "data-testid": "collecte-form-create-error"
};
import { ref, computed, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Modal, TextInput, SelectInput, Checkbox, Button, AutoComplete, } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { useDonationFormStore } from "../../stores/useDonationFormStore";
import { useTakenCampaigns } from "../composables/useTakenCampaigns";
import { DEFAULT_FORM_BRANDING, DEFAULT_FORM_SETTINGS, FORM_CURRENCY_CODES, initialFormCurrency, } from "../../domain/formDefaults";
import { campaignsApi } from "@/modules/communications/infrastructure/api/campaigns-api";
import { resolveApiErrorMessage } from "@/infrastructure/services/ApiErrorService";
import { logger } from "@/infrastructure/services/LoggerService";
import { useWorkspaceCurrency } from "@/shared/composables/useWorkspaceCurrency";
// Backend errorCode -> i18n key for the create failures the manager can recover from this form
// (R70). The expected recoverable failure is a 409 Form.PublicSlug.Conflict (the slug is already
// taken); Form.PublicSlug.Invalid covers a slug the backend rejects that the FE pattern let through.
// Unknown codes fall back to the generic saveFailed key. The broader field-level 422 surfacing lands
// with the branding/settings wizard (T-ADM-005). The codes are the wire contract (DonationFormErrorCodes).
const NO_CAMPAIGNS_OPTION = "__no-campaigns__";
const SLUG_MAX_LENGTH = 128;
const TITLE_MAX_LENGTH = 255;
// Derive a backend-valid slug from an arbitrary title: strip fr/nl diacritics (é/ë/ç…),
// lower-case, collapse any non-alphanumeric run to a single hyphen, trim/collapse hyphens
// and cap at SLUG_MAX_LENGTH. The output always satisfies SLUG_PATTERN and the length bound.
export default /*@__PURE__*/ _defineComponent({
    __name: 'FormCreateModal',
    props: {
        "modelValue": { type: Boolean, ...{ required: true } },
        "modelModifiers": {},
    },
    emits: /*@__PURE__*/ _mergeModels(["created"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const COLLECTE_CREATE_ERROR_KEYS = {
            "Form.PublicSlug.Conflict": "collecte.forms.create.errors.slugConflict",
            "Form.PublicSlug.Invalid": "collecte.forms.create.errors.slugFormat",
            // The slug field has no maxlength and accepts a pasted value, so an over-long slug reaches the
            // command validator (`mb_strlen > 128` -> Form.PublicSlug.TooLong). `validate()` below now refuses
            // it first, but the mapping stays as the safety net R70 asks for: without it a 422 renders the
            // generic saveFailed, whose only affordance is a retry that re-sends the same slug. Mirrors the
            // sibling FormDuplicateModal, which pre-validates the same bound and maps the same code.
            "Form.PublicSlug.TooLong": "collecte.forms.create.errors.slugTooLong",
            // The rest are safety nets — `validate()` pre-checks each of them — but mapping them keeps the
            // FE↔BE contract aligned (R70) so a 422 from the command validator still renders an actionable
            // message instead of the generic saveFailed. Every recoverable code this modal's payload can
            // provoke is listed; leaving some out would make the rule above true only by half.
            "Form.PublicSlug.Required": "collecte.forms.create.errors.slugRequired",
            "Form.Title.Required": "collecte.forms.create.errors.titleRequired",
            "Form.Title.TooLong": "collecte.forms.create.errors.titleTooLong",
            "Form.DefaultLocale.NotSupported": "collecte.forms.create.errors.defaultLocaleNotSupported",
            // 409 — a campaign carries at most ONE live donation form and the picked one already
            // has its own. The picker greys the taken rows, but that grey is a LOWER BOUND (a truncated or
            // failed sweep flags nothing — see `useTakenCampaigns`) and its view of the tenant can be minutes
            // old, so this code is genuinely reachable and its message is the only thing that explains the
            // refusal when it is.
            "Form.Campaign.AlreadyHasForm": "collecte.forms.create.errors.campaignAlreadyHasForm",
        };
        const open = _useModel(__props, "modelValue");
        // `created` carries the new form id so the parent can navigate to the wizard (later
        // increment) or refresh the list. The list view refreshes for now.
        const emit = __emit;
        const { t } = useI18n();
        const store = useDonationFormStore();
        // Names the dialog for assistive tech (`aria-labelledby` -> the `<h2>` above).
        const titleId = useId();
        // Locale codes offered for the form's default + supported locales. fr/nl/en mirror the
        // OnePlatform CRM locales; the labels come from i18n (R29).
        const SUPPORTED_LOCALE_CODES = ["fr", "nl", "en"];
        const { currency: workspaceCurrency, isLoading: workspaceCurrencyLoading, load: loadWorkspaceCurrency, } = useWorkspaceCurrency({ immediate: false });
        const title = ref("");
        const publicSlug = ref("");
        const campaignId = ref("");
        const defaultCurrency = ref(initialFormCurrency(workspaceCurrency.value));
        const defaultLocale = ref("fr");
        const supportedLocales = ref(["fr"]);
        // Once the manager edits the slug by hand, further title edits must stop overwriting it.
        const slugManuallyEdited = ref(false);
        const titleError = ref(null);
        const slugError = ref(null);
        const localeError = ref(null);
        const submitError = ref(null);
        // ─── Campaign async selector ────────────────────────────────────────────────────
        // The campaign can be chosen up front, at creation, so the manager picks it here from a real list
        // (`campaignsApi.getCampaigns`); it stays editable afterwards from the wizard's Campaign tab
        // (PUT /forms/{id}/campaign, driven by `CampaignSection`). AutoComplete's v-model is the selected
        // id; its options carry the friendly name so the picked campaign reads back as a name, not a UUID.
        // Value of the disabled informational row that stands in for an empty campaign list. Not
        // UUID-shaped, so it can never collide with a real campaign id, and `disabled`: OPAL's `select()`
        // early-returns on it while `navigableIndices` drops it from keyboard navigation — so it can be
        // neither clicked nor Enter-selected and can never reach `campaignId`.
        const fetchedCampaignOptions = ref([]);
        const campaignsLoading = ref(false);
        // True once a campaign lookup has COMPLETED (with results, with none, or by failing). Gates the
        // "no campaigns found" row so it can never claim an empty catalogue before anything was fetched.
        const campaignSearchCompleted = ref(false);
        let campaignSearchToken = 0;
        // a campaign carries AT MOST ONE live donation form, so a campaign another live form
        // already holds is offered as a DISABLED row rather than a selection the create call would refuse
        // with 409 `Form.Campaign.AlreadyHasForm`. No `ownCampaignId` exemption here — this modal creates a
        // form, so there is no existing link of its own to spare. The composable owns the two policies that
        // govern the marking (a truncated or failed sweep flags NOTHING) and the reasoning behind them.
        const { load: loadTakenCampaigns, isTaken: isCampaignTaken } = useTakenCampaigns();
        const campaignOptions = computed(() => {
            const options = [
                // Marked HERE rather than in `runCampaignSearch` so a sweep that resolves AFTER a search still
                // repaints the rows already on screen: `isCampaignTaken` reads a ref the composable owns, so
                // this computed subscribes to it and re-runs when the sweep lands. OPAL's `select()` early-returns on `disabled` and `navigableIndices` drops it, so a
                // marked row can be neither clicked nor Enter-selected; the sub-line carries the reason.
                ...fetchedCampaignOptions.value.map((option) => isCampaignTaken(option.value)
                    ? {
                        ...option,
                        disabled: true,
                        description: t("collecte.forms.create.campaignAlreadyUsedHint"),
                    }
                    : option),
            ];
            // Zero-result feedback, gated on a COMPLETED lookup so it never claims an empty catalogue before
            // anything was fetched. A FAILED search also lands here — it clears the list too — and there is no
            // separate "search error" copy to tell the two apart, so this row means "no campaigns to offer",
            // not "the catalogue is empty".
            if (campaignSearchCompleted.value && !campaignsLoading.value && !fetchedCampaignOptions.value.length) {
                options.push({
                    value: NO_CAMPAIGNS_OPTION,
                    label: t("collecte.forms.create.campaignEmpty"),
                    disabled: true,
                });
            }
            return options;
        });
        // The picker's selection → the value the create payload carries. The zero-result row and an
        // untouched field mean "no campaign attached" → undefined, so the form is created campaign-less.
        // Single choke point, so the zero-result row's value cannot reach the API — where a non-UUID
        // campaignId dies at the BFF's Guid binding as an uncoded 400, i.e. a generic toast with nothing
        // actionable in it. The row is already unselectable in OPAL; this keeps that from being the only
        // thing standing between it and the request body.
        function toPersistedCampaignId(selection) {
            const trimmed = selection.trim();
            return trimmed && trimmed !== NO_CAMPAIGNS_OPTION ? trimmed : undefined;
        }
        async function runCampaignSearch(search) {
            const token = ++campaignSearchToken;
            campaignsLoading.value = true;
            try {
                const page = await campaignsApi.getCampaigns({ search: search || undefined, pageSize: 20 });
                if (token !== campaignSearchToken)
                    return; // discard stale response
                fetchedCampaignOptions.value = page.items.map((c) => ({ value: c.id, label: c.name }));
            }
            catch (err) {
                if (token !== campaignSearchToken)
                    return;
                // Silent failure: the dropdown falls back to the "no campaigns found" row (`campaignOptions`
                // cannot tell an empty result from a failed one, and the copy says the former). Log it for
                // diagnostics (R47); note LoggerService only reaches the console in a dev build, so in
                // production this path is diagnosable from the network tab, not from a log.
                logger.error("[FormCreateModal] Campaign search failed", err);
                fetchedCampaignOptions.value = [];
            }
            finally {
                if (token === campaignSearchToken) {
                    campaignsLoading.value = false;
                    campaignSearchCompleted.value = true;
                }
            }
        }
        function onCampaignSearch(query) {
            runCampaignSearch(query);
        }
        // OPAL's × empties the query and the selection but emits no `search`, and its button carries
        // `@mousedown.prevent` so focus stays on the input — with the panel open at any query length it
        // therefore stays open showing the PREVIOUS query's results under a now-empty field. When that
        // query had returned nothing, the panel goes further and asserts "no campaigns found" over an empty
        // query while the organisation has campaigns. Re-run the unfiltered lookup so the open panel always
        // describes the query the manager can actually see.
        function onCampaignClear() {
            runCampaignSearch("");
        }
        // Consume Escape on behalf of whichever OPAL popup the manager is dismissing — the campaign
        // combobox or either currency/locale select — so it never reaches Modal's document listener and
        // destroys the filled form. Runs in the CAPTURE phase (see the template comment): that is what
        // guarantees the `aria-expanded` read happens before OPAL closes the panel and Vue rewrites the
        // attribute. All three controls carry `role="combobox"` + `aria-expanded`, so one predicate covers
        // them; when nothing is open the attribute reads "false" and Escape dismisses the pop-up as before.
        function onFieldEscape(event) {
            const target = event.target;
            if (target?.getAttribute("aria-expanded") === "true") {
                event.preventDefault();
            }
        }
        const currencyOptions = computed(() => FORM_CURRENCY_CODES.map((code) => ({ value: code, label: code })));
        const localeOptions = computed(() => SUPPORTED_LOCALE_CODES.map((code) => ({ value: code, label: t(`collecte.forms.locale.${code}`) })));
        const currencyPicked = ref(false);
        const workspaceCurrencyRequested = ref(false);
        // CONSTRAINT: a form's currency never changes, so Create waits for the workspace one unless the
        // manager picked a currency.
        const awaitingWorkspaceCurrency = computed(() => workspaceCurrencyRequested.value && workspaceCurrencyLoading.value && !currencyPicked.value);
        function onCurrencyPicked(code) {
            defaultCurrency.value = code;
            currencyPicked.value = true;
        }
        // The read resolves after the open: never apply it over a currency the manager already picked.
        async function preselectWorkspaceCurrency() {
            workspaceCurrencyRequested.value = true;
            await loadWorkspaceCurrency();
            if (!currencyPicked.value) {
                defaultCurrency.value = initialFormCurrency(workspaceCurrency.value);
            }
        }
        // Reset the form each time the modal opens so a previous draft never leaks into a new create.
        watch(open, (isOpen) => {
            if (isOpen) {
                title.value = "";
                publicSlug.value = "";
                slugManuallyEdited.value = false;
                campaignId.value = "";
                defaultCurrency.value = initialFormCurrency(workspaceCurrency.value);
                currencyPicked.value = false;
                defaultLocale.value = "fr";
                supportedLocales.value = ["fr"];
                titleError.value = null;
                slugError.value = null;
                localeError.value = null;
                submitError.value = null;
                fetchedCampaignOptions.value = [];
                // Reset with the list: the "no campaigns found" row must not be inherited from a previous
                // session, where it would claim an empty catalogue before this one has fetched anything.
                campaignSearchCompleted.value = false;
                // Seed the campaign picker with the first page so it has content the first time it opens. OPAL's
                // `onFocus` does NOT emit `search` and nothing else populates the options, so without this a
                // focus on an untouched picker opens an empty panel — a manager with 40 campaigns reads "there
                // are no campaigns".
                runCampaignSearch("");
                // Re-collected on EVERY open, not once per session: this modal stays mounted for the life of the
                // list view, so a form created (or archived) since the last open would otherwise leave the
                // markers describing a tenant that no longer exists. Not awaited — the picker opens on the
                // campaign list and the markers repaint through `campaignOptions` when this resolves; it never
                // rejects (it logs and degrades to flagging nothing).
                void loadTakenCampaigns();
                void preselectWorkspaceCurrency();
            }
        });
        function toggleLocale(loc, checked) {
            const set = new Set(supportedLocales.value);
            if (checked) {
                set.add(loc);
            }
            else {
                set.delete(loc);
            }
            supportedLocales.value = SUPPORTED_LOCALE_CODES.filter((c) => set.has(c));
        }
        // Lower-case alphanumeric words separated by single hyphens (mirrors the backend slug rule).
        const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
        // Mirrors the backend slug/title bounds (DonationForm slug max 128, title max 255).
        function slugify(input) {
            return input
                .normalize("NFKD")
                .replace(/[̀-ͯ]/g, "")
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-+|-+$/g, "")
                .replace(/-{2,}/g, "-")
                .slice(0, SLUG_MAX_LENGTH)
                .replace(/-+$/g, "");
        }
        // A manual slug edit stops the title→slug auto-fill (dirty-tracking).
        function onSlugInput(value) {
            publicSlug.value = value;
            slugManuallyEdited.value = true;
        }
        // Auto-fill the slug from the title until the manager edits the slug by hand. The
        // programmatic write here does NOT trip slugManuallyEdited (onSlugInput is the only setter that does).
        watch(title, (t) => {
            if (!slugManuallyEdited.value) {
                publicSlug.value = slugify(t);
            }
        });
        function validate() {
            titleError.value = null;
            slugError.value = null;
            localeError.value = null;
            if (!title.value.trim()) {
                titleError.value = "collecte.forms.create.errors.titleRequired";
            }
            else if (title.value.trim().length > TITLE_MAX_LENGTH) {
                titleError.value = "collecte.forms.create.errors.titleTooLong";
            }
            // SLUG_MAX_LENGTH only ever clamped the slug DERIVED from the title; a hand-typed or pasted slug
            // reached the command validator unchecked and came back as an unmapped 422, i.e. a generic
            // "try again" whose retry re-sent the same slug. The length is checked AHEAD of the pattern, like
            // the sibling FormDuplicateModal and like the command validator itself, so a slug that is both
            // over-long and malformed reports the length — the problem the manager has to fix first.
            const trimmedSlug = publicSlug.value.trim();
            if (!trimmedSlug) {
                slugError.value = "collecte.forms.create.errors.slugRequired";
            }
            else if (trimmedSlug.length > SLUG_MAX_LENGTH) {
                slugError.value = "collecte.forms.create.errors.slugTooLong";
            }
            else if (!SLUG_PATTERN.test(trimmedSlug)) {
                slugError.value = "collecte.forms.create.errors.slugFormat";
            }
            // The campaign is OPTIONAL at create — no validation branch here; a form may be created
            // campaign-less as a Draft and get its campaign later from the management Campaign section.
            // The default locale must be one of the supported locales, so the donor page always has
            // content for the locale it falls back to.
            if (!supportedLocales.value.includes(defaultLocale.value)) {
                localeError.value = "collecte.forms.create.errors.defaultLocaleNotSupported";
            }
            return !titleError.value && !slugError.value && !localeError.value;
        }
        // The starting branding + settings live in `domain/formDefaults.ts` because the campaign-action
        // creation path (`DonationPageCreateModal`) creates forms too and must start them identically —
        // see that file for why the colours are exempt from R78 (stored donor-page config, not app UI).
        const DEFAULT_BRANDING = DEFAULT_FORM_BRANDING;
        const DEFAULT_SETTINGS = DEFAULT_FORM_SETTINGS;
        async function submit() {
            submitError.value = null;
            if (!validate()) {
                return;
            }
            try {
                const ack = await store.createForm({
                    title: title.value.trim(),
                    publicSlug: publicSlug.value.trim(),
                    // Campaign is optional: send the id only when an EXISTING campaign was picked; omit it for an
                    // untouched field, so the form is created campaign-less (Draft with campaignId = null).
                    campaignId: toPersistedCampaignId(campaignId.value),
                    defaultCurrency: defaultCurrency.value,
                    defaultLocale: defaultLocale.value,
                    supportedLocales: supportedLocales.value,
                    branding: { ...DEFAULT_BRANDING },
                    settings: { ...DEFAULT_SETTINGS },
                });
                open.value = false;
                // On 201 hand the new id back to the parent. The detail/wizard route lands in a later
                // increment; until then the list view refreshes so the new form appears.
                emit("created", ack.id);
            }
            catch (e) {
                // Map the backend errorCode to an actionable, localized message (R70) — a slug clash
                // (409 Form.PublicSlug.Conflict) is the create flow's expected recoverable failure and must
                // be actionable, not a generic "save failed". resolveApiErrorMessage returns already-translated
                // text (so the template renders submitError directly) and falls back to saveFailed for unknown
                // codes / network errors (R46 — user feedback, no console logging needed).
                submitError.value = resolveApiErrorMessage(e, COLLECTE_CREATE_ERROR_KEYS, "collecte.forms.create.errors.saveFailed");
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((open).value = $event)),
                size: "md",
                "aria-labelledby": _unref(titleId)
            }, {
                header: _withCtx(() => [
                    _createElementVNode("h2", {
                        id: _unref(titleId),
                        class: "text-xl font-bold text-(--colors-text-heading)",
                        "data-testid": "collecte-form-create-heading"
                    }, _toDisplayString(_ctx.$t("collecte.forms.create.title")), 9 /* TEXT, PROPS */, _hoisted_1)
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: _ctx.$t('common.cancel'),
                        "data-testid": "collecte-form-create-cancel",
                        onClick: _cache[3] || (_cache[3] = ($event) => (open.value = false))
                    }, null, 8 /* PROPS */, ["label"]),
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        label: _ctx.$t('collecte.forms.create.submit'),
                        disabled: _unref(store).isSaving || awaitingWorkspaceCurrency.value,
                        "data-testid": "collecte-form-create-submit",
                        onClick: submit
                    }, null, 8 /* PROPS */, ["label", "disabled"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("p", _hoisted_2, _toDisplayString(_ctx.$t("collecte.forms.create.subtitle")), 1 /* TEXT */),
                    _createElementVNode("div", {
                        class: "space-y-4",
                        onKeydownCapture: _withKeys(onFieldEscape, ["esc"])
                    }, [
                        _createVNode(_unref(TextInput), {
                            modelValue: title.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((title).value = $event)),
                            label: _ctx.$t('collecte.forms.create.titleLabel'),
                            placeholder: _ctx.$t('collecte.forms.create.titlePlaceholder'),
                            error: titleError.value ? _ctx.$t(titleError.value) : undefined,
                            required: "",
                            "full-width": "",
                            "data-testid": "collecte-form-create-title"
                        }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error"]),
                        _createElementVNode("div", null, [
                            _createVNode(_unref(TextInput), {
                                "model-value": publicSlug.value,
                                label: _ctx.$t('collecte.forms.create.slugLabel'),
                                placeholder: _ctx.$t('collecte.forms.create.slugPlaceholder'),
                                error: slugError.value ? _ctx.$t(slugError.value) : undefined,
                                required: "",
                                "full-width": "",
                                "data-testid": "collecte-form-create-slug",
                                "onUpdate:modelValue": onSlugInput
                            }, null, 8 /* PROPS */, ["model-value", "label", "placeholder", "error"]),
                            _createElementVNode("p", _hoisted_3, _toDisplayString(_ctx.$t("collecte.forms.create.slugHint")), 1 /* TEXT */)
                        ]),
                        _createElementVNode("div", null, [
                            _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                modelValue: campaignId.value,
                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((campaignId).value = $event)),
                                label: _ctx.$t('collecte.forms.create.campaignLabel'),
                                placeholder: _ctx.$t('collecte.forms.create.campaignPlaceholder'),
                                options: campaignOptions.value,
                                loading: campaignsLoading.value,
                                required: false,
                                "icon-left": '',
                                "icon-right": '',
                                "results-label": _ctx.$t('collecte.forms.create.campaignResults'),
                                "min-search-length": 0,
                                "keep-input-on-edit": true,
                                "full-width": "",
                                "data-testid": "collecte-form-create-campaign",
                                onSearch: onCampaignSearch,
                                onClear: onCampaignClear
                            }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "results-label"]),
                            _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.$t("collecte.forms.create.campaignOptionalHint")), 1 /* TEXT */)
                        ]),
                        _createVNode(_unref(SelectInput), {
                            "model-value": defaultCurrency.value,
                            label: _ctx.$t('collecte.forms.create.currencyLabel'),
                            options: currencyOptions.value,
                            "full-width": "",
                            "data-testid": "collecte-form-create-currency",
                            "onUpdate:modelValue": onCurrencyPicked
                        }, null, 8 /* PROPS */, ["model-value", "label", "options"]),
                        _createVNode(_unref(SelectInput), {
                            modelValue: defaultLocale.value,
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((defaultLocale).value = $event)),
                            label: _ctx.$t('collecte.forms.create.defaultLocaleLabel'),
                            options: localeOptions.value,
                            error: localeError.value ? _ctx.$t(localeError.value) : undefined,
                            required: "",
                            "full-width": "",
                            "data-testid": "collecte-form-create-default-locale"
                        }, null, 8 /* PROPS */, ["modelValue", "label", "options", "error"]),
                        _createElementVNode("fieldset", null, [
                            _createElementVNode("legend", _hoisted_5, _toDisplayString(_ctx.$t("collecte.forms.create.supportedLocalesLabel")), 1 /* TEXT */),
                            _createElementVNode("div", _hoisted_6, [
                                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(SUPPORTED_LOCALE_CODES, (loc) => {
                                    return _createVNode(_unref(Checkbox), {
                                        key: loc,
                                        "model-value": supportedLocales.value.includes(loc),
                                        label: _ctx.$t(`collecte.forms.locale.${loc}`),
                                        "data-testid": `collecte-form-create-locale-${loc}`,
                                        "onUpdate:modelValue": (v) => toggleLocale(loc, !!v)
                                    }, null, 8 /* PROPS */, ["model-value", "label", "data-testid", "onUpdate:modelValue"]);
                                }), 64 /* STABLE_FRAGMENT */))
                            ])
                        ]),
                        (submitError.value)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(submitError.value), 1 /* TEXT */))
                            : _createCommentVNode("v-if", true)
                    ], 32 /* NEED_HYDRATION */)
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "aria-labelledby"]));
        };
    }
});
